import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router";
import { api, takeReturnTo } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Wordmark } from "@/components/Wordmark";
import { AUTH_VERIFY } from "@/constants/testIds";
import { Loader2, CircleCheck, CircleAlert } from "lucide-react";

export default function AuthVerify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [error, setError] = useState("");
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      if (!token) {
        setState("error");
        setError("No token in URL.");
        return;
      }
      // Remove the bearer credential before any network request or third-party
      // navigation can observe the address bar, history, or referrer.
      window.history.replaceState({}, document.title, window.location.pathname);
      try {
        const { data } = await api.post("/auth/magic/verify", { token });
        await loginWithToken(data.access_token, data.user);
        setState("ok");
        const returnTo = takeReturnTo();
        const homeByRole = data.user.role === "admin" ? "/admin" : "/client";
        // Only honor returnTo if it matches the role prefix, otherwise use role home.
        const safeTarget =
          returnTo &&
          ((data.user.role === "admin" && returnTo.startsWith("/admin")) ||
            (data.user.role === "client" && returnTo.startsWith("/client")))
            ? returnTo
            : homeByRole;
        setTimeout(() => { nav(safeTarget, { replace: true }); }, 500);
      } catch (err) {
        setState("error");
        setError(err?.response?.data?.detail || "Could not verify link.");
      }
    })();
  }, [token, loginWithToken, nav]);

  return (
    <div
      data-testid={AUTH_VERIFY.container}
      className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
    >
      <div className="fs-card p-10 max-w-md w-full text-center">
        <Wordmark className="text-base" />
        <div className="mt-8" data-testid={AUTH_VERIFY.status}>
          {state === "verifying" && (
            <>
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
              <div className="mt-4 font-outfit text-xl font-semibold text-slate-900">
                Verifying your secure link…
              </div>
              <p className="mt-2 text-sm text-slate-500">Hold tight, this only takes a second.</p>
            </>
          )}
          {state === "ok" && (
            <>
              <CircleCheck className="w-10 h-10 mx-auto text-emerald-600" />
              <div className="mt-4 font-outfit text-xl font-semibold text-slate-900">You're in.</div>
              <p className="mt-2 text-sm text-slate-500">Redirecting to your dashboard…</p>
            </>
          )}
          {state === "error" && (
            <>
              <CircleAlert className="w-10 h-10 mx-auto text-red-600" />
              <div className="mt-4 font-outfit text-xl font-semibold text-slate-900">
                Link problem
              </div>
              <p className="mt-2 text-sm text-slate-600">{error}</p>
              <Link
                to="/signin"
                className="inline-block mt-6 fs-btn-primary px-4 py-2 rounded-md text-sm"
              >
                Request a new link
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
